import type { Payment, PaymentInput, PaymentMethod, PriceAdjustment, PriceAdjustmentInput } from "@/features/payments/types";
import { createIdempotencyKey } from "@/shared/lib/idempotency";
import { api } from "@/shared/lib/api";
import { useOfflineQueue } from "@/shared/offline/offlineQueue";

export async function listPayments(orderId?: string): Promise<Payment[]> {
  return api.get<Payment[]>("payments", "list", { order_id: orderId ?? "" });
}

export async function createPayment(input: PaymentInput): Promise<Payment | null> {
  const idempotencyKey = createIdempotencyKey("payment");

  if (!navigator.onLine) {
    await useOfflineQueue.getState().enqueue({
      id: idempotencyKey,
      type: "payment.create",
      payload: input,
      createdAt: new Date().toISOString(),
      attempts: 0
    });
    return null;
  }

  return createPaymentOnline(input, idempotencyKey);
}

/**
 * Insert via the idempotent RPC — safe to call repeatedly with the same key,
 * so the sync engine can replay queued mutations without double-writing.
 */
export async function createPaymentOnline(input: PaymentInput, idempotencyKey: string): Promise<Payment> {
  return api.post<Payment>("payments", "create", {
    idempotencyKey,
    orderId: input.orderId,
    amountKobo: input.amountKobo,
    method: input.method,
    reference: input.reference ?? null,
    note: input.note ?? null,
  });
}

export type PaymentCorrectionInput = {
  /** The payment being corrected. */
  paymentId: string;
  orderId: string;
  /** What the payment should have been, in kobo. */
  correctAmountKobo: number;
  /** Original recorded amount, in kobo — used to compute the delta entry. */
  originalAmountKobo: number;
  note: string;
};

/**
 * Payments become immutable 24h after entry (DB trigger). Corrections are new
 * ledger rows holding the delta, linked via correction_for_payment_id, so the
 * ledger stays append-only and totals still sum correctly.
 */
export async function createPaymentCorrection(input: PaymentCorrectionInput): Promise<Payment> {
  return api.post<Payment>("payments", "correct", input);
}

export async function listPriceAdjustments(orderId: string): Promise<PriceAdjustment[]> {
  return api.get<PriceAdjustment[]>("payments", "adjustments", { order_id: orderId });
}

export async function createPriceAdjustment(input: PriceAdjustmentInput): Promise<PriceAdjustment> {
  return api.post<PriceAdjustment>("payments", "adjust-price", input);
}

export async function createPaymentRefund(input: { orderId: string; amountKobo: number; method?: PaymentMethod; reference?: string; note?: string }): Promise<Payment> {
  return api.post<Payment>("payments", "refund", input);
}
